// Web Push subscription flow — request permission, register SW, subscribe,
// and POST the subscription to the backend. Called once per session; no-op
// on unsupported browsers (Safari iOS <16.4, etc). Kept in one place so any
// screen can nudge techs to enable notifications.

import { api } from "@/lib/api";

// URL-safe base64 → Uint8Array (WebPush needs the VAPID key in raw bytes).
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

export function pushPermission() {
  return pushSupported() ? Notification.permission : "unsupported";
}

let _swRegPromise = null;
async function _registerSw() {
  if (!_swRegPromise) _swRegPromise = navigator.serviceWorker.register("/sw.js");
  return _swRegPromise;
}

// Ask permission (only once per browser session — user's yes/no is remembered
// by the browser thereafter) and subscribe. Returns "granted" | "denied" |
// "default" | "unsupported" | "error".
export async function enablePushNotifications() {
  if (!pushSupported()) return "unsupported";
  try {
    let perm = Notification.permission;
    if (perm === "default") {
      perm = await Notification.requestPermission();
    }
    if (perm !== "granted") return perm;

    const reg = await _registerSw();
    // Wait for it to be active — subscribe() fails if the SW isn't ready.
    if (!reg.active) await navigator.serviceWorker.ready;

    // Grab VAPID public key from backend so it can rotate without a rebuild.
    const { data: vapid } = await api.get("/push/vapid-public-key");
    if (!vapid?.key) return "error";

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid.key),
      });
    }

    const json = sub.toJSON();
    await api.post("/push/subscribe", {
      endpoint: json.endpoint,
      keys: json.keys || {},
      user_agent: navigator.userAgent || "",
    });
    return "granted";
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("push enable failed:", e);
    return "error";
  }
}

export async function disablePushNotifications() {
  if (!pushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration("/sw.js");
    const sub = await reg?.pushManager.getSubscription();
    if (sub) {
      await api.post("/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => {});
      await sub.unsubscribe();
    }
  } catch (_) { /* silent */ }
}
